import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import FlashcardCard from '../components/FlashcardCard';
import { flashcards } from '../data/flashcards';

const GeorgiaRealEstateExam: React.FC = () => {
  useEffect(() => {
    // Update page title and meta tags for SEO
    document.title = 'Georgia Real Estate Exam Prep | 500+ Flashcards & Practice Tests | RealEstart';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Pass your Georgia real estate exam with confidence! Interactive flashcards, practice tests, and study materials specifically designed for Georgia. 500+ questions, mobile-friendly, 3-day free trial.');
    }

    // Add structured data for better SEO
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      "name": "RealEstart Georgia Real Estate Exam Prep",
      "description": "Comprehensive Georgia real estate exam preparation with interactive flashcards and practice tests",
      "url": "https://realestart.io/georgia-real-estate-exam",
      "sameAs": [
        "https://realestart.io"
      ],
      "offers": {
        "@type": "Offer",
        "name": "Georgia Real Estate Exam Prep",
        "description": "Interactive flashcards and practice tests for Georgia real estate licensing exam",
        "price": "0",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      // Cleanup
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const sampleCard = flashcards[0];

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">R</span>
              </div>
              <span className="text-xl font-bold text-gray-900">RealEstart.io</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/auth" className="text-gray-600 hover:text-[#FF6B6B] transition-colors">Sign In</Link>
              <Link to="/checkout?plan=trial" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg">Start Free Trial</Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold mb-6">
                Pass Your <span className="text-yellow-300">Georgia</span> Real Estate Exam
              </h1>
              <p className="text-xl mb-8 text-white/90">
                Master Georgia-specific real estate laws, contracts, and regulations with our comprehensive study platform. 
                Interactive flashcards, practice tests, and detailed explanations designed specifically for the Georgia exam.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/checkout?plan=trial&state=GA" 
                  className="bg-white text-[#FF6B6B] px-8 py-4 rounded-full text-lg font-semibold hover:shadow-lg transition-all transform hover:scale-105 text-center"
                >
                  Start 3-Day Free Trial
                </Link>
                <Link 
                  to="/demo" 
                  className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-[#FF6B6B] transition-all text-center"
                >
                  Try Demo First
                </Link>
              </div>
              <div className="mt-8 flex items-center space-x-6 text-sm">
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-[#27AE60] rounded-full"></span>
                  <span>500+ Georgia-Specific Flashcards</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-[#27AE60] rounded-full"></span>
                  <span>152 Practice Questions</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-[#27AE60] rounded-full"></span>
                  <span>Mobile-Friendly</span>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <FlashcardCard
                card={sampleCard}
                showAnswer={false}
                onCardClick={() => {}}
                onSwipe={() => {}}
                isAnimating={false}
                darkMode={true}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Georgia-Specific Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Georgia Real Estate Exam Overview</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The Georgia real estate exam consists of 152 multiple-choice questions covering both national and Georgia-specific topics. 
              You need 70% correct answers to pass.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-2xl">
              <h3 className="text-xl font-semibold mb-4 text-[#FF6B6B]">Georgia Real Estate Law</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Georgia Real Estate Commission (GREC) regulations</li>
                <li>• Georgia-specific disclosure requirements</li>
                <li>• Property tax exemptions and homestead laws</li>
                <li>• Georgia condominium and HOA regulations</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-8 rounded-2xl">
              <h3 className="text-xl font-semibold mb-4 text-[#FF8E53]">Contracts & Agency</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Georgia contract forms and requirements</li>
                <li>• Agency relationships and disclosures</li>
                <li>• Brokerage agreements and commissions</li>
                <li>• Fair housing laws in Georgia</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-8 rounded-2xl">
              <h3 className="text-xl font-semibold mb-4 text-[#2C3E50]">Property & Finance</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Georgia property tax system</li>
                <li>• Flood insurance requirements</li>
                <li>• Natural disaster considerations</li>
                <li>• Georgia mortgage and lending laws</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose RealEstart for Georgia?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our Georgia-specific content is created by real estate professionals and updated regularly to reflect current laws and regulations.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF6B6B] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Georgia-Specific Content</h3>
              <p className="text-sm text-gray-600">All flashcards and questions focus on Georgia laws, regulations, and exam requirements.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF8E53] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📱</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Study Anywhere</h3>
              <p className="text-sm text-gray-600">Mobile-friendly platform lets you study during breaks, commutes, or anywhere you have time.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#27AE60] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Track Progress</h3>
              <p className="text-sm text-gray-600">Monitor your performance and identify areas that need more attention.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#2C3E50] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Smart Learning</h3>
              <p className="text-sm text-gray-600">Spaced repetition algorithm shows you difficult topics more frequently.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">Ready to Pass Your Georgia Real Estate Exam?</h2>
          <p className="text-xl mb-8 text-white/90">
            Join thousands of successful Georgia real estate professionals who used RealEstart to pass their exam on the first try.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/checkout?plan=trial&state=GA"
              className="bg-white text-[#FF6B6B] px-8 py-4 rounded-full text-lg font-semibold hover:shadow-lg transition-all transform hover:scale-105 inline-block"
            >
              Start Your Free Trial
            </Link>
            <Link 
              to="/demo"
              className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-[#FF6B6B] transition-all inline-block"
            >
              Try Demo First
            </Link>
          </div>
          <p className="mt-4 text-sm text-white/70">No credit card required • Cancel anytime</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-2xl font-bold mb-4">🏠 RealEstart.io</div>
            <p className="text-gray-400 mb-4">
              Your trusted companion for Georgia real estate exam preparation.
            </p>
            <p className="text-sm text-gray-500">
              &copy; 2024 RealEstart.io. This is a study aid and is not affiliated with any official exam provider.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GeorgiaRealEstateExam;
